import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, ListItemIcon, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { request } from "app/network";
import Swal from "sweetalert2";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface ReorderDisplayItemsDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
}

const SortableItem = ({ id, item, darkMode }: { id: any, item: any, darkMode?: boolean }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: darkMode ? "#202940" : "#fff",
        marginBottom: "8px",
        borderRadius: "8px",
        border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #ddd"
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <ListItem sx={{ px: 2, py: 1 }}>
                <ListItemIcon sx={{ minWidth: "40px" }}>
                    <DragIndicatorIcon sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }} />
                </ListItemIcon>
                <MDBox component="img" src={item.goodsImgS} width="40px" borderRadius="md" alt={item.goodsName} mr={2} />
                <ListItemText
                    primary={
                        <MDTypography variant="subtitle2" color={darkMode ? "white" : "dark"}>
                            {item.goodsName}
                        </MDTypography>
                    }
                    secondary={
                        <MDTypography variant="caption" color="text">
                            {item.brandName} | {item.categoryName || "카테고리 없음"}
                        </MDTypography>
                    }
                />
            </ListItem>
        </div>
    );
};

const ReorderDisplayItemsDialog = ({ open, onClose, onSuccess, darkMode }: ReorderDisplayItemsDialogProps) => {
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (open) {
            fetchList();
        }
    }, [open]);

    const fetchList = async () => {
        setLoading(true);
        try {
            // Fetch ALL items (large size) to reorder globally
            // DisplayOrder defaults to end, so normal list order is by DisplayOrder
            const response = await request("Giftishow.GetDisplayList", {
                paramz: { start: 1, size: 1000 } // Fetch enough items
            });
            if (response.data && response.data.list) {
                setItems(response.data.list);
            }
        } catch (error) {
            console.error(error);
            Swal.fire("오류", "목록을 불러오지 못했습니다.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (event: any) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setItems((items) => {
                const oldIndex = items.findIndex((item) => item.displaySeq === active.id);
                const newIndex = items.findIndex((item) => item.displaySeq === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
        setActiveId(null);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Prepare updates: Assign DisplayOrder based on current array index + 1
            const updates = items.map((item, index) => ({
                displaySeq: item.displaySeq,
                displayOrder: index + 1
            }));

            await request("Giftishow.UpdateDisplayOrder", {
                paramz: updates // Note: networkManager behavior with paramz vs body need to confirm.
                // Assuming "paramz" is passed as JSON body for PUT
            });

            Swal.fire("성공", "순서가 변경되었습니다.", "success");
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error(error);
            Swal.fire("실패", "저장에 실패했습니다.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    p: 2,
                    backgroundColor: darkMode ? "#1a2035" : "#fff",
                    height: "80vh",
                    display: "flex",
                    flexDirection: "column"
                }
            }}
        >
            <DialogTitle sx={{ color: darkMode ? '#fff' : '#344767' }}>
                <MDTypography variant="h5" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                    상품 전시 순서 변경
                </MDTypography>
                <MDTypography variant="caption" color="text">
                    드래그하여 순서를 변경하세요.
                </MDTypography>
            </DialogTitle>

            <DialogContent sx={{ flex: 1, overflowY: "auto", p: 1 }}>
                {loading && items.length === 0 ? (
                    <MDBox display="flex" justifyContent="center" p={5}>
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={items.map(item => item.displaySeq)}
                            strategy={verticalListSortingStrategy}
                        >
                            {items.map((item) => (
                                <SortableItem key={item.displaySeq} id={item.displaySeq} item={item} darkMode={darkMode} />
                            ))}
                        </SortableContext>
                    </DndContext>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="secondary">취소</Button>
                <MDButton variant="gradient" color="info" onClick={handleSave} disabled={loading}>
                    저장
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default ReorderDisplayItemsDialog;
